import { Box, Flex, Grid, Image, Stack, Text, Tooltip } from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { Part } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { ReactNode } from 'react'

import type { EditableTotalProps } from '@/containers/EditableTotal'
import { EditableTotal } from '@/containers/EditableTotal'
import { useApp } from '@/hooks/useApp'

import moneyBillIcon from '../../../images/money-bill-duotone-solid.svg'
import warningIcon from '../../../images/warning-icon.svg'


interface OfferPartProps extends Omit<Part, 'name' | 'offerId' | 'description' | 'riskId' | 'riskType' | 'valid' | 'included'> {
  name?: string
  label?: ReactNode
  unavailableText?: string
  children?: Part[]
  showPrices?: boolean
  editableProps?: EditableTotalProps
  valid?: boolean
}

export const OfferPart = (props: OfferPartProps) => {
  const {
    name,
    nameNew,
    label,
    price,
    unavailableText = 'Není k dispozici',
    errors,
    children,
    showPrices = true,
    editableProps,
    valid = true
  } = props

  const anyErrors = errors && errors.length > 0
  const formattedPrice =
    (anyErrors || !valid) ? undefined : priceFormat(price)

  const { product } = useApp()

  return (
    <Grid
      sx={{
        p: 2,
        pr: 0,
        gap: 0.5,
        borderBottom: '1px solid',
        borderBottomColor: 'neutral.50',
        _last: {
          borderBottom: 'none',
        }
      }}
    >
      <Flex
        sx={{
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ fontSize: 'sm' }}>{label}</Box>

        {editableProps?.isEditable &&
          <EditableTotal
            size="sm"
            fontWeight='bolder'
            color="transparent"
            {...editableProps}
            price={price ?? undefined}
          />}

        {!editableProps?.isEditable && showPrices && formattedPrice && (
          <Text sx={{ fontSize: 'sm', fontWeight: 'bold' }}>
            {formattedPrice}
          </Text>
        )}

        {!editableProps?.isEditable && showPrices && !formattedPrice && (
          <Text
            sx={{
              color: 'red.500',
              fontSize: 'xs',
              fontWeight: 'bold',
              lineHeight: '1.3125rem',
            }}
          >
            {unavailableText}
          </Text>
        )}
      </Flex>

      <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
        <Flex gap={2} alignItems='center'>
          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>{nameNew ?? name}</Text>

          {product == Product.VehicleLeasingInsurance &&
            <OfferPartComplicity {...props} />
          }
        </Flex>

        {!formattedPrice && (anyErrors || !valid) && (
          <Tooltip
            hasArrow
            label={anyErrors ? (
              <Flex direction="column">
                {errors?.map((x) => <Text key={x.message}>{x.message}</Text>)}
              </Flex>
            ) : undefined
            }
            placement="top"
          >
            <Image src={warningIcon} alt="" />
          </Tooltip>
        )}
      </Flex>

      {children &&
        children.map((p) => (
          <Flex key={p.partId} sx={{ ml: 3, fontSize: 'sm' }}>
            <Text>{p.name}</Text>
          </Flex>
        ))}
    </Grid>
  )
}

const OfferPartComplicity = ({ complicityFix, complicityMinimum, complicityPercentage }: OfferPartProps) => {

  if (!complicityFix && !complicityMinimum && !complicityPercentage)
    return null

  return <CustomTooltip hasArrow p={2.5} maxW='20ch'
    trigger='clickHover'
    label={<Stack>
      <Text fontSize='xs' fontWeight='medium'>Spoluúčast</Text>
      <Text fontWeight='semibold'>
        {complicityFix && priceFormat(complicityFix)}
        {complicityPercentage && `${complicityPercentage} %`}
        {complicityMinimum && ` min. ${priceFormat(complicityMinimum)}`}
      </Text>

      {/** TODO: Poznámka ke spoluúčasti */}
      {/* <Text fontSize='sm' fontStyle='oblique' >
         <InfoOutlineIcon h={3} mr={1.5} mb={1} />
          bez spoluúčasti při opravě scelením
        </Text> */}
    </Stack>}>
    <Image src={moneyBillIcon} h={4} w='auto' />
  </CustomTooltip>
}
